import '@jcodigital/jcore-utils';

/*
 * Mobile vh fix.
 */
let vh = window.innerHeight * 0.01;
document.documentElement.style.setProperty('--vh', `${vh}px`);

// We listen to the resize event
window.addEventListener('resize', () => {
	// We execute the same script as before
	let vh = window.innerHeight * 0.01;
	document.documentElement.style.setProperty('--vh', `${vh}px`);
});

/*
 * Scroll to top functionality
 */
let scrollButton: HTMLElement | null;

function footerInit() {
	scrollButton = document.getElementById('scroll-to-top');
	if (scrollButton) {
		window.addEventListener('scroll', scrollFunction);
		scrollButton.addEventListener('click', () => {
			document.body.scrollTop = 0; // For Safari
			document.documentElement.scrollTop = 0;
		});
	}
}

function scrollFunction() {
	if (
		document.body.scrollTop > 600 ||
		document.documentElement.scrollTop > 600
	) {
		scrollButton.style.opacity = 1;
	} else {
		scrollButton.style.opacity = 0;
	}
}
/* End Scroll to top */

/**
 * Masonry Grid
 */
window.addEventListener('DOMContentLoaded', () => {
	footerInit();
	resizeMasonryGrid();
	const config = { attributes: false, childList: true, subtree: false };
	document.querySelectorAll('.masonry-grid').forEach((grid) => {
		const observer = new MutationObserver(resizeMasonryGrid);
		observer.observe(grid, config);
	});
});
window.addEventListener('resize', () => {
	resizeMasonryGrid();
});

function resizeMasonryGrid() {
	document.querySelectorAll('.masonry-grid').forEach((grid) => {
		const rowHeight = parseInt(
			window.getComputedStyle(grid).getPropertyValue('grid-auto-rows')
		);
		const rowGap = parseInt(
			window.getComputedStyle(grid).getPropertyValue('grid-row-gap')
		);
		for (const item of grid.children) {
			let height = rowGap;
			for (const part of item.children) {
				const style = getComputedStyle(part);
				height += parseInt(style.marginTop);
				height += part.offsetHeight;
				height += parseInt(style.marginBottom);
			}
			const rowSpan = Math.ceil(height / (rowHeight + rowGap));
			item.style.gridRowEnd = 'span ' + rowSpan;
		}
	});
}
/* End Masonry Grid */

/**
 * Read more toggles for jcore grid columns
 *
 * Opt in by adding the `read-more-columns` class to a jcore/grid block. Each
 * column then collapses behind a "read more" toggle, but only when it is
 * actually taller than the collapsed height. Short columns stay open and keep
 * the toggle hidden.
 */
const readMoreLabels = {
	more: 'Read more +',
	less: 'Show less −',
	// Localised labels from wp_localize_script(), see includes/theme-support.php.
	...((window as any).jcoreReadMore ?? {}),
};

/** Parse a computed backgroundColor into channels, or null if fully transparent. */
function parseColor(value: string) {
	const parts = value.match(/[\d.]+/g);

	if (!parts || parts.length < 3) {
		return null;
	}

	const [r, g, b] = parts.map(Number);
	const a = parts.length > 3 ? Number(parts[3]) : 1;

	return a === 0 ? null : { r, g, b, a };
}

type Channels = { r: number; g: number; b: number; a: number };

/**
 * The fade-out has to blend into whatever sits behind the column, which CSS
 * cannot read from an ancestor. Walk up collecting backgrounds until one is
 * opaque, then flatten them, so a translucent tint over white resolves to the
 * solid colour the visitor actually sees rather than a fade you can read through.
 */
function resolveFadeColor(column: HTMLElement) {
	const layers: Channels[] = [];
	let el: HTMLElement | null = column;
	let base: Channels = { r: 255, g: 255, b: 255, a: 1 };

	while (el && el !== document.documentElement) {
		const color = parseColor(getComputedStyle(el).backgroundColor);

		if (color) {
			if (color.a >= 1) {
				base = color;
				break;
			}

			layers.push(color);
		}

		el = el.parentElement;
	}

	// Composite outermost-first so each tint lands on what is already behind it.
	const flat = layers.reduceRight(
		(under, over) => ({
			r: over.r * over.a + under.r * (1 - over.a),
			g: over.g * over.a + under.g * (1 - over.a),
			b: over.b * over.a + under.b * (1 - over.a),
			a: 1,
		}),
		base
	);

	const rgb = [flat.r, flat.g, flat.b].map((c) => Math.round(c)).join(', ');
	column.style.setProperty('--read-more-fade-color', `rgb(${rgb})`);
}

function setupColumnReadMore() {
	const columns = document.querySelectorAll<HTMLElement>(
		'.wp-block-jcore-grid.read-more-columns > .wp-block-jcore-column'
	);

	columns.forEach((column) => {
		if (column.querySelector(':scope > .read-more-container')) {
			return;
		}

		const container = document.createElement('div');
		container.className = 'read-more-container';
		container.append(...column.childNodes);

		const toggle = document.createElement('button');
		toggle.className = 'read-more-toggle';
		toggle.type = 'button';
		toggle.hidden = true;
		toggle.setAttribute('aria-expanded', 'false');
		toggle.textContent = readMoreLabels.more;

		column.append(container, toggle);
		resolveFadeColor(column);

		toggle.addEventListener('click', () => {
			const expanded = container.classList.toggle('is-expanded');

			toggle.setAttribute('aria-expanded', String(expanded));
			toggle.textContent = expanded ? readMoreLabels.less : readMoreLabels.more;
		});

		updateColumnReadMore(container, toggle);
	});
}

/**
 * How much hidden content is worth a toggle: half a line of text. Below that
 * there is nothing meaningful to reveal, and it absorbs the couple of pixels by
 * which a Range's line boxes overshoot the element's own box.
 */
function overflowTolerance(container: HTMLElement) {
	const styles = getComputedStyle(container);
	const line = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5;

	return (line || 24) / 2;
}

/**
 * Keep the toggle only if the collapsed height actually clips the column's
 * content. Columns the visitor already expanded are left alone.
 */
function updateColumnReadMore(container: HTMLElement, toggle: HTMLButtonElement) {
	if (container.classList.contains('is-expanded')) {
		return;
	}

	container.classList.add('is-collapsible');

	// Measure the content box with a Range rather than scrollHeight: scrollHeight
	// is rounded to whole pixels (Firefox reports up to 3px of overflow that is
	// not there, Chrome 1px) and it counts trailing margin, including a margin
	// collapsing out of the last child, so a column whose text fits exactly would
	// still offer a "read more" that expands to reveal only whitespace.
	const range = document.createRange();
	range.selectNodeContents(container);
	const overflow =
		range.getBoundingClientRect().bottom - container.getBoundingClientRect().bottom;

	if (overflow <= overflowTolerance(container)) {
		container.classList.remove('is-collapsible');
		toggle.hidden = true;
		return;
	}

	toggle.hidden = false;
}

function refreshColumnReadMore() {
	document
		.querySelectorAll<HTMLElement>(
			'.wp-block-jcore-grid.read-more-columns > .wp-block-jcore-column'
		)
		.forEach((column) => {
			const container = column.querySelector<HTMLElement>(':scope > .read-more-container');
			const toggle = column.querySelector<HTMLButtonElement>(':scope > .read-more-toggle');

			if (container && toggle) {
				updateColumnReadMore(container, toggle);
			}
		});
}

window.addEventListener('DOMContentLoaded', setupColumnReadMore);

// A first measurement at DOMContentLoaded is taken before the webfonts and any
// images have been applied, and both change how tall the text ends up being.
document.fonts?.ready.then(refreshColumnReadMore);
window.addEventListener('load', refreshColumnReadMore);

// The columns reflow at the grid breakpoints, so what fits changes with width.
let readMoreResizeTimer: ReturnType<typeof setTimeout>;
window.addEventListener('resize', () => {
	clearTimeout(readMoreResizeTimer);
	readMoreResizeTimer = setTimeout(refreshColumnReadMore, 150);
});
/* End Read more toggles */
